import type { Auftrag, Briefing } from '@awf/shared';
import { FELDSAETZE, type FeldSpalte } from '../forms/field-sets.js';
import { useVolltextDialog, VergroessernKnopf } from '../ui/volltext-dialog.js';
import stile from './briefing-karte.module.css';

export interface BriefingFeld {
  name: string;
  wert: string;
  kernfeld: boolean;
}

function anzeigeWert(feld: FeldSpalte, briefing: Briefing): string | null {
  const roh = briefing[feld.name as keyof Briefing];
  if (feld.art === 'schalter') return roh === true ? 'Ja' : 'Nein';
  if (typeof roh !== 'string' || !roh.trim()) return null;
  if (feld.art === 'auswahl') return feld.optionen?.find((o) => o.wert === roh)?.text ?? roh;
  return roh;
}

/**
 * Die Felder, mit denen der Auftrag gestartet wurde, in der Reihenfolge und mit den
 * Beschriftungen des Startformulars (`FELDSAETZE`): so liest sich das Briefing genau wie das,
 * was man eingegeben hat. Leere Freitextfelder fehlen, Schalter stehen immer da (Ja/Nein).
 * Ein Auftrag von vor dem 2026-10-13 traegt kein Briefing; dann steht nur das Thema da.
 */
export function briefingFelder(auftrag: Auftrag): BriefingFeld[] {
  const briefing = auftrag.briefing;
  if (!briefing) {
    const name = auftrag.typ === 'pdp' ? 'PDP-URL' : 'Thema';
    return [{ name, wert: auftrag.thema, kernfeld: true }];
  }
  const spalten = FELDSAETZE[auftrag.typ];
  const mitKern = spalten.some((f) => f.kernfeld);
  return spalten.flatMap((feld) => {
    const wert = anzeigeWert(feld, briefing);
    const kernfeld = !mitKern || feld.kernfeld === true;
    return wert === null ? [] : [{ name: feld.beschriftung, wert, kernfeld }];
  });
}

/**
 * "Ihr Briefing fuer diesen Auftrag" wie `AuftragBriefing` der Content-Factory
 * (auftrag-detail/auftrag-briefing.tsx): Bordeaux-Schleier, jedes Feld auf drei Zeilen
 * gekappt, unten rechts der Vergroessern-Knopf fuer das ganze Briefing ungekuerzt.
 */
export function BriefingKarte({ auftrag }: { auftrag: Auftrag }) {
  const felder = briefingFelder(auftrag);
  const { oeffnen, dialog } = useVolltextDialog();

  return (
    <section className={stile.karte} aria-labelledby="briefing-titel">
      <h2 id="briefing-titel" className={stile.titel}>
        Ihr Briefing für diesen Auftrag
      </h2>
      <dl className={stile.liste}>
        {felder
          .filter((feld) => feld.kernfeld)
          .map((feld) => (
            <div key={feld.name}>
              <dt className={stile.feldName}>{feld.name}</dt>
              <dd className={stile.feldWert} title={feld.wert}>
                {feld.wert}
              </dd>
            </div>
          ))}
      </dl>
      <VergroessernKnopf
        label="Briefing"
        onClick={() =>
          oeffnen(
            'Ihr Briefing für diesen Auftrag',
            <dl className={stile.volltextListe}>
              {felder.map((feld) => (
                <div key={feld.name}>
                  <dt className={stile.feldName}>{feld.name}</dt>
                  <dd className={stile.volltextWert}>{feld.wert}</dd>
                </div>
              ))}
            </dl>,
          )
        }
      />
      {dialog}
    </section>
  );
}
